import {
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { ASSETS_BUCKET, RENDERED_SLIDES_BUCKET } from '../../lib/storage';
import { COARSE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';
import type {
  CommunityZones,
  LogoPosition,
  PictureTreatment,
  SlideBackground,
  SlideLayoutTemplate,
  SlidePhotoBox,
  SlidePoint,
  SlideTheme,
} from '../../types/api';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';
import { LOGO_SIZE, presetLogoPoint, roundFraction } from './slideGeometry';
import {
  CARD_PADDING,
  CARD_RADIUS,
  PANEL_FALLBACK,
  QUOTE_MARK_FRACTION,
  SLIDE_TEMPLATES,
  TEMPLATE_SAFE_AREA,
  cardFillCss,
  hasTextShadow,
  overlayCss,
  scrimColorFor,
  type SlideTemplateConfig,
} from './slideTemplates';
import {
  AnniversaryPhoto,
  GridPreview,
  MenuPreview,
  OfferPreview,
  PortraitDisc,
} from './SlideItemLayers';
import {
  ANNIV_ARC_ALPHA,
  ANNIV_DEFAULT_PHOTO_BOX,
  ANNIV_PHOTO_MIN_SIZE,
  ANNIV_ARC_CENTER,
  ANNIV_ARC_RADII,
  ANNIV_ARC_WIDTH,
  ANNIV_BADGE_BOTTOM,
  ANNIV_BADGE_LEFT,
  ANNIV_BADGE_SUB_FONT,
  ANNIV_BADGE_TITLE_FONT,
  ANNIV_NAME_FIRST_FONT,
  ANNIV_NAME_REST_FONT,
  ANNIV_NAME_TOP,
  ANNIV_QUOTE_FONT,
  ANNIV_QUOTE_RULE_WIDTH,
  ANNIV_TEXT_LEFT,
  ANNIV_TEXT_WIDTH,
  anniversaryBadgeFill,
  anniversaryBadgeInk,
  ITEM_FOCAL_POINTS,
  ITEM_GLOW_ALPHA,
  ITEM_GLOW_SCALE,
  ITEM_RING_ALPHAS,
  ITEM_RING_RADII,
  PORTRAIT_DIAMETER,
  PROFILE_DIVIDER_HEIGHT,
  PROFILE_DIVIDER_WIDTH,
  SPLIT_CALLOUT_GAP,
  SPLIT_CALLOUT_NUMBER_FONT,
  SPLIT_CALLOUT_UNIT_FONT,
  SPLIT_COLUMN_LEFT,
  SPLIT_COLUMN_WIDTH,
  SPLIT_EDGE_BAR_WIDTH,
  SPLIT_NAME_FIRST_FONT,
  SPLIT_NAME_REST_FONT,
  SPLIT_PANEL_FRACTION,
  SPLIT_PORTRAIT_CENTER_Y,
  SPLIT_PORTRAIT_DIAMETER,
  SUBTITLE_FONT,
  isFlatBackground,
  rgbCss,
  splitColors,
  splitValue,
} from './slideItemGeometry';
import { iconBox, iconGlyph, patternBackgroundCss } from './slideSources';
import { drawnTreatment, pictureBox, replacesDuotone } from './slideTreatments';
import { TreatedPicture } from './TreatedPicture';
import type { SlideDraftItem } from './useSlideDraft';
import { useLayerDrag } from './useLayerDrag';

/** The canvas layers a pointer (or the nudge pad) can move. */
export type CanvasLayer = 'caption' | 'logo' | 'photo';

/** What the canvas hands its parent: the selected layer's arrow-key move. */
export interface SlideCanvasControl {
  /** Moves the selected layer by a fraction of the stage; nothing when none is. */
  nudge: (dx: number, dy: number) => void;
}

/** Fallback anchor while a logo has neither a custom point nor a preset. */
const ORIGIN: SlidePoint = { x: 0, y: 0 };

/** Where a template-placed block is assumed to sit until the DOM says otherwise. */
const TEMPLATE_ORIGIN: SlidePoint = { x: TEMPLATE_SAFE_AREA, y: TEMPLATE_SAFE_AREA };

/**
 * A community template's design and zone map, resolved by the parent from the
 * global gallery. Null when the slide is not on a community template — or when
 * the template has since left the gallery, in which case the stage degrades to
 * the classic block over the stored background.
 */
export interface CommunityPreview {
  zones: CommunityZones;
  /** Public preview URL of the author's design, or null when it has none. */
  imageUrl: string | null;
}

/**
 * The paint behind the text, as close as CSS gets to the render template.
 *
 * A slot template does not cover the canvas with the picture: the picture is
 * fitted into its own box (drawn by `.slide-canvas__slot`) and the rest of the
 * slide is the theme's flat panel, exactly as the renderer builds it. A flat
 * colour, a gradient or a pattern fills the canvas whatever the template — there is no
 * picture to slot, which is the state the `image_slot_empty` guardrail notes.
 * A treated picture (anything but full bleed) is drawn by `TreatedPicture`
 * over the same flat panel, so the background is the panel then too.
 */
function backgroundStyle(
  background: SlideBackground,
  url: string | null,
  usesPanel: boolean,
  panelFill: string,
): CSSProperties {
  switch (background.kind) {
    case 'color':
      return { background: background.color };
    case 'gradient':
      return { background: `linear-gradient(135deg, ${background.from}, ${background.to})` };
    case 'pattern':
      return patternBackgroundCss(background);
    case 'asset':
    case 'generated':
      if (usesPanel) return { background: panelFill };
      return url === null ? {} : { backgroundImage: `url("${url}")` };
  }
}

/**
 * The caption's box when the template owns it — no `text_position` in sight.
 *
 * `center` means centred inside the template's own region, which for every
 * full-bleed layout is the whole canvas: the three original anchors resolve to
 * exactly the numbers they resolved to before regions existed.
 */
function templateCaptionStyle(template: SlideTemplateConfig): CSSProperties {
  const left = `${template.textBox.left * 100}%`;
  const inset = `${TEMPLATE_SAFE_AREA * 100}%`;

  switch (template.anchorY) {
    case 'top':
      return { left, top: inset };
    case 'bottom':
      return { left, bottom: inset };
    case 'center': {
      const middle = (template.region.top + template.region.bottom) / 2;
      return { left, top: `${middle * 100}%`, transform: 'translateY(-50%)' };
    }
  }
}

/**
 * Where the band treatment is allowed to fall.
 *
 * On a full-bleed layout it is the whole slide, as it always was. On a slot
 * layout it is the text region only: the renderer paints its bands full width
 * and then puts the slot's pixels back, and confining the div is the cheap way
 * to say the same thing in CSS — the picture beside the type never gets washed.
 */
function scrimBoxStyle(template: SlideTemplateConfig): CSSProperties {
  if (template.imageBox === null) return {};
  return {
    inset: 'auto',
    left: `${(template.textBox.left - CARD_PADDING) * 100}%`,
    width: `${(template.textBox.width + CARD_PADDING * 2) * 100}%`,
    top: `${template.region.top * 100}%`,
    height: `${(template.region.bottom - template.region.top) * 100}%`,
  };
}

/** The achievement callout: a display numeral over its unit in tracked caps. */
function ValueCallout({
  value,
  accent,
  numberFont,
  unitFont,
}: {
  value: string;
  accent: string;
  numberFont: number;
  unitFont: number;
}) {
  const { number, unit } = splitValue(value);
  if (number.length === 0) return null;
  return (
    <span className="slide-canvas__callout" aria-hidden="true">
      <span
        className="slide-canvas__callout-number"
        style={{ color: accent, fontSize: `${numberFont * 100}cqw` }}
      >
        {number}
      </span>
      {unit ? (
        <span className="slide-canvas__callout-unit" style={{ fontSize: `${unitFont * 100}cqw` }}>
          {unit}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The slide's icon, one Lucide glyph in its placement box. The renderer
 * falls back to the text colour when the accent is too faint on the ground;
 * the preview always paints the accent, and the PNG stays the truth.
 */
function IconGlyph({
  codepoint,
  box,
  color,
}: {
  codepoint: number;
  box: { left: number; top: number; width: number; height: number };
  color: string;
}) {
  return (
    <span
      className="slide-canvas__icon"
      style={{
        left: `${box.left * 100}%`,
        top: `${box.top * 100}%`,
        width: `${box.width * 100}%`,
        height: `${box.height * 100}%`,
        fontSize: `${box.height * 100}cqw`,
        color,
      }}
      aria-hidden="true"
    >
      {iconGlyph(codepoint)}
    </span>
  );
}

interface SlideCanvasProps {
  headline: string;
  body: string | null;
  /** Kicker above the headline, painted in the theme's accent. */
  eyebrow?: string | null;
  /** The frozen tokens the slide renders with; undefined previews as classic. */
  theme?: SlideTheme | null;
  background: SlideBackground;
  /** Server-owned, shown but never edited here. */
  textColor: string;
  /**
   * Where the caption block sits, or null when the template places it. Null is
   * an unpinned slide: the words reflow, the block moves with them.
   */
  textPosition: SlidePoint | null;
  /** Which layout places the block, and shapes the scrim behind it. */
  layoutTemplate?: SlideLayoutTemplate;
  /** True once the renderer's corrective pass darkened the scrim. */
  scrimApplied?: boolean;
  logoPosition: LogoPosition;
  /** Null means the preset applies. */
  logoPoint: SlidePoint | null;
  /** The brand's logo in the `assets` bucket, or null for a placeholder chip. */
  logoPath: string | null;
  /**
   * The structured items an item template arranges — the live editor rows, so
   * the preview moves with every keystroke. Empty on every classic slide.
   */
  items?: readonly SlideDraftItem[];
  /** The community template behind a `community` slide, when the gallery still has it. */
  community?: CommunityPreview | null;
  /** The anniversary poster's photo box; null means the template's own. */
  photoBox?: SlidePhotoBox | null;
  /**
   * How the picture is presented. Ignored on a colour or gradient background
   * and on the item and community compositions, exactly as the renderer does.
   */
  pictureTreatment?: PictureTreatment;
  /**
   * The slide's icon as its Lucide codepoint, or null for none. Drawn in the
   * theme accent at the template's `ICON_PLACEMENT` box; never a drag target.
   */
  iconCodepoint?: number | null;
  disabled?: boolean;
  onTextPositionChange: (next: SlidePoint) => void;
  onLogoPointChange: (next: SlidePoint) => void;
  /** Raised when the photo is moved or resized. Always a concrete box. */
  onPhotoBoxChange?: (next: SlidePhotoBox) => void;
  /**
   * The layer last focused or tapped: the one the nudge pad moves. Under a
   * finger it is also the only layer that takes a drag; the others let the
   * page scroll. Null when none is.
   */
  selectedLayer?: CanvasLayer | null;
  /** Raised when a layer is focused or tapped, and with null on the bare stage. */
  onSelectLayer?: (layer: CanvasLayer | null) => void;
  /** Receives the nudge for the selected layer: the arrow keys' own move. */
  controlRef?: Ref<SlideCanvasControl>;
}

/**
 * The slide as movable layers: background, picture slot, scrim, caption block,
 * logo box — plus the item compositions (portraits, grids, menu and offer
 * rows, the split profile panel) and a community template's zone-placed
 * layers, drawn as decoration around the two blocks that move.
 *
 * This is a deliberate approximation, not a renderer. It exists so placement is
 * something the user does with a pointer instead of typing coordinates, and the
 * fractions it produces are exactly what the server draws with — but the fonts,
 * wrapping and spacing here are the browser's, and the PNG the pipeline returns
 * on save remains the only truth. The editor says so under the stage.
 *
 * What is *not* on the canvas matters as much (SRS §3): no colour picker, no
 * font control, no text sizing, no layer reordering. Two blocks move; nothing
 * else about the design is the user's to change.
 */
export function SlideCanvas({
  headline,
  body,
  eyebrow = null,
  theme = null,
  background,
  textColor,
  textPosition,
  layoutTemplate = 'auto',
  scrimApplied = false,
  logoPosition,
  logoPoint,
  logoPath,
  items = [],
  community = null,
  photoBox = null,
  pictureTreatment = 'full_bleed',
  iconCodepoint = null,
  disabled = false,
  onTextPositionChange,
  onLogoPointChange,
  onPhotoBoxChange,
  selectedLayer = null,
  onSelectLayer,
  controlRef,
}: SlideCanvasProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLDivElement>(null);
  // Select-then-drag (mobile plan, ruling 14): under a finger a layer takes the
  // gesture only once it is selected, so the page still scrolls across the
  // stage. Without a selection owner there is nothing to select, so every layer
  // stays armed as before.
  const isCoarse = useMediaQuery(COARSE_QUERY);
  const isArmed = (layer: CanvasLayer) =>
    !isCoarse || onSelectLayer === undefined || selectedLayer === layer;
  /**
   * Where the template actually put the block, measured off the DOM. It is the
   * starting point a keyboard nudge moves away from, so the first arrow press
   * on an unpinned block continues from the layout instead of jumping to a
   * guess. Held in state, not a ref, because the drag hook closes over it.
   */
  const [templatePoint, setTemplatePoint] = useState<SlidePoint>(TEMPLATE_ORIGIN);

  const template = SLIDE_TEMPLATES[layoutTemplate];
  const isTemplatePlaced = textPosition === null;
  const accentColor = theme?.accent ?? textColor;
  const firstItem = items.length > 0 ? (items[0] ?? null) : null;

  // The three special compositions, in the renderer's own precedence: a
  // community slide is the author's design with zone-placed layers; a profile
  // with an item on a flat brand background takes the split panel; the other
  // item templates stack their rows into the caption block.
  // Only a v1 map is drawn here: a v2 design is previewed by the shared
  // `CommunityDesignPreview`, which the layer editor mounts in this stage's place.
  const communityV1 =
    layoutTemplate === 'community' && community !== null && community.zones.v === 1
      ? community.zones
      : null;
  const isCommunity = communityV1 !== null;
  const isSplit =
    !isCommunity &&
    layoutTemplate === 'profile' &&
    isFlatBackground(background) &&
    firstItem !== null;
  // The anniversary poster is its own composition on any background; without
  // an item it degrades to the classic stack, exactly as the renderer does.
  const isAnniversary = !isCommunity && layoutTemplate === 'anniversary' && firstItem !== null;
  const isItemStack = !isCommunity && !isSplit && !isAnniversary && template.kind === 'item';
  // An item slide on a flat brand background is composed (glow + rings), not
  // washed: the renderer skips the base scrim there, and on a community design.
  const flatItemCanvas =
    template.kind === 'item' && layoutTemplate !== 'anniversary' && isFlatBackground(background);
  const skipBaseScrim = isCommunity || isSplit || isAnniversary || flatItemCanvas;

  // A pull-quote is a card layout by definition, whatever the theme says — but
  // the card is drawn from the theme's own tokens, so with no theme in hand
  // there is nothing to draw and the preview stays honest about it.
  const showCard =
    !isCommunity &&
    !isSplit &&
    !isAnniversary &&
    theme !== null &&
    (theme.card_enabled || template.forceCard);
  // The picture gets a box of its own only when there is a picture: a flat
  // colour or a gradient fills the canvas and leaves the slot empty.
  const usesSlot =
    template.imageBox !== null && (background.kind === 'asset' || background.kind === 'generated');
  // What the stage draws: the requested treatment, or full bleed where the
  // background has no picture or the composition ignores treatments.
  const treatment = drawnTreatment(pictureTreatment, background, layoutTemplate);
  const isTreated = treatment !== 'full_bleed';

  const backgroundPath =
    background.kind === 'asset' || background.kind === 'generated' ? background.asset_path : null;
  const backgroundBucket = background.kind === 'generated' ? RENDERED_SLIDES_BUCKET : ASSETS_BUCKET;
  const { url: backgroundUrl } = useSignedAssetUrl(backgroundPath, backgroundBucket);
  const { url: logoUrl } = useSignedAssetUrl(logoPath);

  // On a community slide the preset means "the zone the author labeled", not a
  // corner of the canvas; dragging still pins an explicit point like anywhere.
  const zones = communityV1;
  const presetPoint =
    zones !== null
      ? logoPosition === 'none'
        ? null
        : { x: zones.logo.x, y: zones.logo.y }
      : presetLogoPoint(
          // The poster has one designed logo home — the bottom-right of the
          // name column — so every preset previews there, as the renderer
          // resolves it. Dragging still pins a free point.
          isAnniversary && logoPosition !== 'none' ? 'bottom_right' : logoPosition,
          template.textBox,
        );
  const effectiveLogoPoint = logoPoint ?? presetPoint;
  const hasLogoBox = logoPosition !== 'none' && effectiveLogoPoint !== null;
  /** The logo box is the labeled zone while it still follows the zone map. */
  const zoneLogoBox = zones !== null && logoPoint === null ? zones.logo : null;

  useLayoutEffect(() => {
    if (!isTemplatePlaced) return;
    const stage = stageRef.current;
    const caption = captionRef.current;
    if (!stage || !caption) return;

    function measure() {
      if (!stage || !caption) return;
      const stageRect = stage.getBoundingClientRect();
      if (stageRect.width === 0 || stageRect.height === 0) return;
      const captionRect = caption.getBoundingClientRect();
      const measured: SlidePoint = {
        x: roundFraction((captionRect.left - stageRect.left) / stageRect.width),
        y: roundFraction((captionRect.top - stageRect.top) / stageRect.height),
      };
      setTemplatePoint((current) =>
        current.x === measured.x && current.y === measured.y ? current : measured,
      );
    }

    // The block moves when the words reflow it and when the stage resizes, not
    // only when React re-renders, so the DOM is what gets watched.
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(caption);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [isTemplatePlaced, layoutTemplate]);

  const caption = useLayerDrag(
    stageRef,
    textPosition ?? templatePoint,
    onTextPositionChange,
    disabled,
    isArmed('caption'),
  );
  const logo = useLayerDrag(
    stageRef,
    effectiveLogoPoint ?? ORIGIN,
    onLogoPointChange,
    disabled || !hasLogoBox,
    isArmed('logo'),
  );

  // The poster's photo is the third draggable layer: a drag moves its box, the
  // corner handle resizes it. The box the stage draws when none is stored is
  // the template's own, so the first drag starts from the truth.
  const effectivePhotoBox: SlidePhotoBox = photoBox ?? ANNIV_DEFAULT_PHOTO_BOX;
  const hasPhotoLayer = isAnniversary && firstItem !== null && onPhotoBoxChange !== undefined;
  const photoDrag = useLayerDrag(
    stageRef,
    { x: effectivePhotoBox.x, y: effectivePhotoBox.y },
    (next) => onPhotoBoxChange?.({ ...effectivePhotoBox, x: next.x, y: next.y }),
    disabled || !hasPhotoLayer,
    isArmed('photo'),
  );

  // Rebuilt every render on purpose: the nudge closes over this render's
  // positions, exactly as the arrow keys' handler does.
  useImperativeHandle(controlRef, () => ({
    nudge(dx: number, dy: number) {
      if (selectedLayer === 'caption' && captionRef.current) {
        caption.nudge(captionRef.current, dx, dy);
      } else if (selectedLayer === 'logo' && logoRef.current) {
        logo.nudge(logoRef.current, dx, dy);
      } else if (selectedLayer === 'photo' && photoRef.current) {
        photoDrag.nudge(photoRef.current, dx, dy);
      }
    },
  }));

  /** A press on the bare stage, not on a layer, lets go of the selection. */
  function handleStagePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (onSelectLayer === undefined) return;
    const target = event.target;
    if (target instanceof Element && target.closest('.slide-canvas__layer') !== null) return;
    onSelectLayer(null);
  }

  /** One in-flight resize: the stage box measured once, the latest size. */
  const photoResizeRef = useRef<{
    stage: { left: number; top: number; width: number; height: number };
    element: HTMLElement;
    next: { w: number; h: number };
  } | null>(null);

  function startPhotoResize(event: ReactPointerEvent<HTMLButtonElement>) {
    if (disabled || event.button !== 0) return;
    event.stopPropagation();
    const stage = stageRef.current;
    const element = event.currentTarget.parentElement;
    if (!stage || !element) return;
    const rect = stage.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    photoResizeRef.current = {
      stage: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      element,
      next: { w: effectivePhotoBox.w, h: effectivePhotoBox.h },
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function movePhotoResize(event: ReactPointerEvent<HTMLButtonElement>) {
    const session = photoResizeRef.current;
    if (session === null) return;
    event.stopPropagation();
    const width = (event.clientX - session.stage.left) / session.stage.width - effectivePhotoBox.x;
    const height = (event.clientY - session.stage.top) / session.stage.height - effectivePhotoBox.y;
    const w = Math.min(1 - effectivePhotoBox.x, Math.max(ANNIV_PHOTO_MIN_SIZE, width));
    const h = Math.min(1 - effectivePhotoBox.y, Math.max(ANNIV_PHOTO_MIN_SIZE, height));
    session.next = { w: roundFraction(w), h: roundFraction(h) };
    session.element.style.width = `${w * 100}%`;
    session.element.style.height = `${h * 100}%`;
  }

  function endPhotoResize(event: ReactPointerEvent<HTMLButtonElement>) {
    const session = photoResizeRef.current;
    if (session === null) return;
    event.stopPropagation();
    photoResizeRef.current = null;
    // Back to what React last wrote; the commit re-renders with the new box.
    session.element.style.width = `${effectivePhotoBox.w * 100}%`;
    session.element.style.height = `${effectivePhotoBox.h * 100}%`;
    if (session.next.w !== effectivePhotoBox.w || session.next.h !== effectivePhotoBox.h) {
      onPhotoBoxChange?.({ ...effectivePhotoBox, ...session.next });
    }
  }

  const showVerticalGuide = caption.guides.x || logo.guides.x;
  const showHorizontalGuide = caption.guides.y || logo.guides.y;

  function layerClassName(name: string, isDragging: boolean, layer: CanvasLayer): string {
    const classes = ['slide-canvas__layer', `slide-canvas__${name}`];
    if (selectedLayer === layer) classes.push('slide-canvas__layer--selected');
    if (isDragging) classes.push('slide-canvas__layer--dragging');
    if (disabled) classes.push('slide-canvas__layer--locked');
    return classes.join(' ');
  }

  function captionClassName(): string {
    const classes = [layerClassName('caption', caption.isDragging, 'caption')];
    // Mirror of the renderer: a shadow goes under the light text only.
    if (hasTextShadow(textColor)) classes.push('slide-canvas__caption--shadowed');
    if (showCard) classes.push('slide-canvas__caption--card');
    if (isCommunity) classes.push('slide-canvas__caption--zone');
    return classes.join(' ');
  }

  /** Where the caption box sits and how wide it wraps, per composition. */
  function captionBoxStyle(): CSSProperties {
    if (zones !== null) {
      const width = `${zones.text.w * 100}%`;
      if (!isTemplatePlaced) {
        return { left: `${textPosition.x * 100}%`, top: `${textPosition.y * 100}%`, width };
      }
      // The labeled box, content vertically centred inside it — the renderer's
      // own default placement for a community caption.
      return {
        left: `${zones.text.x * 100}%`,
        top: `${zones.text.y * 100}%`,
        width,
        height: `${zones.text.h * 100}%`,
        justifyContent: 'center',
      };
    }
    if (isSplit) {
      const width = `${SPLIT_COLUMN_WIDTH * 100}%`;
      if (!isTemplatePlaced) {
        return { left: `${textPosition.x * 100}%`, top: `${textPosition.y * 100}%`, width };
      }
      // The type column: the open half, vertically centred.
      return {
        left: `${SPLIT_COLUMN_LEFT * 100}%`,
        top: '50%',
        transform: 'translateY(-50%)',
        width,
      };
    }
    if (isAnniversary) {
      const width = `${ANNIV_TEXT_WIDTH * 100}%`;
      if (!isTemplatePlaced) {
        return { left: `${textPosition.x * 100}%`, top: `${textPosition.y * 100}%`, width };
      }
      // The name column, upper right — the renderer's own default.
      return { left: `${ANNIV_TEXT_LEFT * 100}%`, top: `${ANNIV_NAME_TOP * 100}%`, width };
    }
    return {
      ...(isTemplatePlaced
        ? templateCaptionStyle(template)
        : { left: `${textPosition.x * 100}%`, top: `${textPosition.y * 100}%` }),
      // The column is the template's, pinned or not: a dragged block keeps the
      // wrap width the renderer will wrap it at.
      width: `${template.textBox.width * 100}%`,
    };
  }

  const captionAlign = zones !== null ? zones.align : template.align;
  const captionStyle: CSSProperties = {
    ...captionBoxStyle(),
    color: textColor,
    textAlign: captionAlign,
    ...(showCard && theme !== null
      ? {
          background: cardFillCss(theme, textColor),
          borderRadius: `${CARD_RADIUS * 100}cqw`,
          ...(theme.card_border_width > 0
            ? {
                border: `1px solid ${theme.card_border_color ?? scrimColorFor(textColor)}`,
              }
            : {}),
          ...(theme.card_shadow ? { boxShadow: '0 0.6cqw 1.2cqw rgba(0, 0, 0, 0.35)' } : {}),
        }
      : {}),
  };

  // The base scrim is skipped on the composed canvases exactly as the renderer
  // skips it; the corrective boost still shows wherever the render needed one.
  // Tritone and halftone are colour maps of their own and replace duotone.
  const requestedOverlay = theme?.overlay_style ?? 'faded';
  const overlay = overlayCss(
    skipBaseScrim || (requestedOverlay === 'duotone' && replacesDuotone(treatment))
      ? 'clear'
      : requestedOverlay,
    template,
    scrimColorFor(textColor),
    scrimApplied,
    theme?.accent,
  );

  // The renderer draws the item's own title as the headline on a profile, and
  // on a community slide whose zone map places a portrait.
  const showsItemName =
    firstItem !== null &&
    firstItem.title.trim().length > 0 &&
    (layoutTemplate === 'profile' || (zones !== null && zones.portrait !== null));
  const displayHeadline = showsItemName && firstItem !== null ? firstItem.title.trim() : headline;

  // Mirror of the renderer's headline ladder — its first rung only. The browser
  // does not step the type down when the words overflow, which is one more
  // thing the "preview approximate" line under the stage exists to say.
  const headlineStyle: CSSProperties = {
    fontSize: `${template.headlineSize * 100}cqw`,
    ...(template.uppercase ? { textTransform: 'uppercase' } : {}),
    ...(template.maxLines === 1 ? { whiteSpace: 'nowrap' } : {}),
  };

  const subtitleText =
    (layoutTemplate === 'profile' || isAnniversary) && firstItem !== null
      ? firstItem.subtitle.trim()
      : '';

  /** The accent rule + tracked designation line under a profile's name. */
  function designation(): ReactNode {
    if (subtitleText.length === 0) return null;
    return (
      <>
        <span
          className="slide-canvas__divider"
          style={{
            background: accentColor,
            width: `${PROFILE_DIVIDER_WIDTH * 100}cqw`,
            height: `${Math.max(PROFILE_DIVIDER_HEIGHT * 100, 0.2)}cqw`,
            ...(captionAlign === 'center' ? { alignSelf: 'center' } : {}),
          }}
          aria-hidden="true"
        />
        <span className="slide-canvas__subtitle" style={{ fontSize: `${SUBTITLE_FONT * 100}cqw` }}>
          {subtitleText}
        </span>
      </>
    );
  }

  /** The caption block's contents for the split-panel profile composition. */
  function splitCaptionContent(): ReactNode {
    const words = (firstItem?.title ?? headline).trim().split(/\s+/).filter(Boolean);
    const firstName = words[0] ?? '';
    const restName = words.slice(1).join(' ');
    return (
      <>
        {eyebrow === null || eyebrow.length === 0 ? null : (
          <span className="slide-canvas__eyebrow" style={{ color: accentColor }}>
            {eyebrow}
          </span>
        )}
        <span
          className="slide-canvas__headline slide-canvas__headline--split"
          style={{ fontSize: `${SPLIT_NAME_FIRST_FONT * 100}cqw` }}
        >
          {firstName}
        </span>
        {restName ? (
          <span
            className="slide-canvas__headline slide-canvas__headline--split"
            style={{ fontSize: `${SPLIT_NAME_REST_FONT * 100}cqw` }}
          >
            {restName}
          </span>
        ) : null}
        {designation()}
        {body === null || body.length === 0 ? null : (
          <span className="slide-canvas__body" style={{ fontSize: '2.78cqw' }}>
            {body}
          </span>
        )}
      </>
    );
  }

  /** The caption block's contents for the anniversary poster: the name stack. */
  function anniversaryCaptionContent(): ReactNode {
    const words = (firstItem?.title ?? headline).trim().split(/\s+/).filter(Boolean);
    const firstName = words[0] ?? '';
    const restName = words.slice(1).join(' ');
    return (
      <>
        <span
          className="slide-canvas__headline slide-canvas__headline--split"
          style={{ fontSize: `${ANNIV_NAME_FIRST_FONT * 100}cqw` }}
        >
          {firstName}
        </span>
        {restName ? (
          <span
            className="slide-canvas__headline slide-canvas__headline--split"
            style={{ fontSize: `${ANNIV_NAME_REST_FONT * 100}cqw` }}
          >
            {restName}
          </span>
        ) : null}
        {subtitleText ? (
          <span
            className="slide-canvas__subtitle"
            style={{ fontSize: `${SUBTITLE_FONT * 100}cqw` }}
          >
            {subtitleText}
          </span>
        ) : null}
      </>
    );
  }

  /** The anniversary poster's decoration: halftone, arcs, photo arch, quote, badge. */
  function anniversaryDecor(): ReactNode {
    if (!isAnniversary || firstItem === null) return null;
    const badgeTitle = headline.trim();
    const badgeSub = (eyebrow ?? '').trim();
    return (
      <div className="slide-canvas__decor" aria-hidden="true">
        {isFlatBackground(background) ? <span className="slide-canvas__halftone" /> : null}
        {ANNIV_ARC_RADII.map((radius) => (
          <span
            className="slide-canvas__item-ring"
            key={radius}
            style={{
              left: `${(ANNIV_ARC_CENTER.x - radius) * 100}%`,
              top: `${(ANNIV_ARC_CENTER.y - radius) * 100}%`,
              width: `${radius * 2 * 100}%`,
              height: `${radius * 2 * 100}%`,
              borderWidth: `${ANNIV_ARC_WIDTH * 100}cqw`,
              borderColor: `color-mix(in srgb, ${accentColor} ${Math.round(
                ANNIV_ARC_ALPHA * 100,
              )}%, transparent)`,
            }}
          />
        ))}
        {body ? (
          <span
            className="slide-canvas__anniv-quote"
            style={{ color: textColor, fontSize: `${ANNIV_QUOTE_FONT * 100}cqw` }}
          >
            <span
              className="slide-canvas__anniv-rule"
              style={{ width: `${ANNIV_QUOTE_RULE_WIDTH * 100}cqw`, background: textColor }}
            />
            <span>{body}</span>
          </span>
        ) : null}
        {badgeTitle || badgeSub ? (
          <span
            className="slide-canvas__anniv-badge"
            style={{
              left: `${ANNIV_BADGE_LEFT * 100}%`,
              bottom: `${ANNIV_BADGE_BOTTOM * 100}%`,
              background: anniversaryBadgeFill(background),
              color: anniversaryBadgeInk(background),
            }}
          >
            {badgeTitle ? (
              <span
                className="slide-canvas__anniv-badge-title"
                style={{ fontSize: `${ANNIV_BADGE_TITLE_FONT * 100}cqw` }}
              >
                {badgeTitle}
              </span>
            ) : null}
            {badgeSub ? (
              <span
                className="slide-canvas__anniv-badge-sub"
                style={{ fontSize: `${ANNIV_BADGE_SUB_FONT * 100}cqw` }}
              >
                {badgeSub}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    );
  }

  /** The classic stack, with the item rows folded in where the template draws them. */
  function stackCaptionContent(): ReactNode {
    return (
      <>
        {template.quoteMark ? (
          <span
            className="slide-canvas__quote-mark"
            style={{
              color: theme?.accent ?? textColor,
              fontSize: `${QUOTE_MARK_FRACTION * 100}cqw`,
            }}
            aria-hidden="true"
          >
            &ldquo;
          </span>
        ) : null}
        {eyebrow === null || eyebrow.length === 0 ? null : (
          <span className="slide-canvas__eyebrow" style={{ color: accentColor }}>
            {eyebrow}
          </span>
        )}
        {isItemStack && layoutTemplate === 'profile' && firstItem !== null ? (
          <span
            className="slide-canvas__portrait-slot"
            style={captionAlign === 'center' ? { alignSelf: 'center' } : {}}
          >
            <PortraitDisc
              assetPath={firstItem.asset_path}
              title={firstItem.title}
              accent={accentColor}
              diameter={PORTRAIT_DIAMETER}
            />
          </span>
        ) : null}
        <span className="slide-canvas__headline" style={headlineStyle}>
          {displayHeadline}
        </span>
        {isItemStack && layoutTemplate === 'profile' ? designation() : null}
        {isItemStack && layoutTemplate === 'profile_grid' && items.length > 0 ? (
          <GridPreview items={items} accent={accentColor} />
        ) : null}
        {isItemStack && layoutTemplate === 'menu_list' && items.length > 0 ? (
          <MenuPreview items={items} />
        ) : null}
        {isItemStack && layoutTemplate === 'offer' && items.length > 0 ? (
          <OfferPreview items={items} accent={accentColor} />
        ) : null}
        {body === null || body.length === 0 ? null : (
          <span
            className="slide-canvas__body"
            style={{ fontSize: `${template.bodySize * 100}cqw` }}
          >
            {body}
          </span>
        )}
      </>
    );
  }

  /** The split composition's decoration: panel, edge bar, portrait, callout. */
  function splitDecor(): ReactNode {
    if (!isSplit || firstItem === null) return null;
    const { panel } = splitColors(background);
    const diameter = SPLIT_PORTRAIT_DIAMETER;
    const portraitLeft = SPLIT_PANEL_FRACTION / 2 - diameter / 2;
    const portraitTop = SPLIT_PORTRAIT_CENTER_Y - diameter / 2;
    return (
      <div className="slide-canvas__decor" aria-hidden="true">
        <span
          className="slide-canvas__split-panel"
          style={{ width: `${SPLIT_PANEL_FRACTION * 100}%`, background: rgbCss(panel) }}
        />
        <span
          className="slide-canvas__split-bar"
          style={{ width: `${SPLIT_EDGE_BAR_WIDTH * 100}%`, background: accentColor }}
        />
        <span
          className="slide-canvas__decor-anchor"
          style={{ left: `${portraitLeft * 100}%`, top: `${portraitTop * 100}%` }}
        >
          <PortraitDisc
            assetPath={firstItem.asset_path}
            title={firstItem.title}
            accent={accentColor}
            diameter={diameter}
          />
        </span>
        {firstItem.value.trim() ? (
          <span
            className="slide-canvas__decor-anchor slide-canvas__split-callout"
            style={{
              left: 0,
              width: `${SPLIT_PANEL_FRACTION * 100}%`,
              top: `${(portraitTop + diameter + SPLIT_CALLOUT_GAP) * 100}%`,
            }}
          >
            <ValueCallout
              value={firstItem.value}
              accent={accentColor}
              numberFont={SPLIT_CALLOUT_NUMBER_FONT}
              unitFont={SPLIT_CALLOUT_UNIT_FONT}
            />
          </span>
        ) : null}
      </div>
    );
  }

  /** The accent glow and rings an item slide gets on a flat brand background. */
  function itemDecor(): ReactNode {
    if (!flatItemCanvas || isSplit) return null;
    const focal = ITEM_FOCAL_POINTS[layoutTemplate] ?? { x: 0.5, y: 0.4 };
    return (
      <div className="slide-canvas__decor" aria-hidden="true">
        <span
          className="slide-canvas__item-glow"
          style={{
            left: `${(focal.x - ITEM_GLOW_SCALE / 2) * 100}%`,
            top: `${(focal.y - ITEM_GLOW_SCALE / 2) * 100}%`,
            width: `${ITEM_GLOW_SCALE * 100}%`,
            height: `${ITEM_GLOW_SCALE * 100}%`,
            backgroundImage: `radial-gradient(circle, color-mix(in srgb, ${accentColor} ${Math.round(
              ITEM_GLOW_ALPHA * 100,
            )}%, transparent) 0%, transparent 70%)`,
          }}
        />
        {ITEM_RING_RADII.map((radius, index) => (
          <span
            className="slide-canvas__item-ring"
            key={radius}
            style={{
              left: `${(focal.x - radius) * 100}%`,
              top: `${(focal.y - radius) * 100}%`,
              width: `${radius * 2 * 100}%`,
              height: `${radius * 2 * 100}%`,
              borderColor: `color-mix(in srgb, ${accentColor} ${Math.round(
                (ITEM_RING_ALPHAS[index] ?? 0.1) * 100,
              )}%, transparent)`,
            }}
          />
        ))}
      </div>
    );
  }

  /** A community template's own zone-placed layers: portrait and value callout. */
  function communityDecor(): ReactNode {
    if (zones === null) return null;
    return (
      <div className="slide-canvas__decor" aria-hidden="true">
        {zones.portrait !== null && firstItem !== null ? (
          <span
            className="slide-canvas__decor-anchor"
            style={{
              left: `${(zones.portrait.cx - zones.portrait.d / 2) * 100}%`,
              top: `${(zones.portrait.cy - zones.portrait.d / 2) * 100}%`,
            }}
          >
            <PortraitDisc
              assetPath={firstItem.asset_path}
              title={firstItem.title}
              accent={accentColor}
              diameter={zones.portrait.d}
            />
          </span>
        ) : null}
        {zones.value !== null && firstItem !== null && firstItem.value.trim() ? (
          <span
            className="slide-canvas__decor-anchor slide-canvas__split-callout"
            style={{
              left: `${zones.value.x * 100}%`,
              top: `${zones.value.y * 100}%`,
              width: `${zones.value.w * 100}%`,
              height: `${zones.value.h * 100}%`,
              justifyContent: 'center',
            }}
          >
            <ValueCallout
              value={firstItem.value}
              accent={accentColor}
              numberFont={Math.min(SPLIT_CALLOUT_NUMBER_FONT, zones.value.h)}
              unitFont={SPLIT_CALLOUT_UNIT_FONT}
            />
          </span>
        ) : null}
      </div>
    );
  }

  const communityBackground: CSSProperties | null =
    isCommunity && community !== null && community.imageUrl !== null
      ? { backgroundImage: `url("${community.imageUrl}")` }
      : null;
  const splitBackground: CSSProperties | null = isSplit
    ? { background: rgbCss(splitColors(background).field) }
    : null;

  return (
    <div className="slide-canvas" ref={stageRef} onPointerDown={handleStagePointerDown}>
      <div
        className="slide-canvas__background"
        style={
          communityBackground ??
          splitBackground ??
          backgroundStyle(
            background,
            backgroundUrl,
            usesSlot || isTreated,
            theme?.panel_fill ?? PANEL_FALLBACK,
          )
        }
        aria-hidden="true"
      />

      {isTreated && backgroundUrl !== null ? (
        <TreatedPicture
          treatment={treatment}
          url={backgroundUrl}
          box={pictureBox(layoutTemplate)}
          isSlot={usesSlot}
          captionAnchor={template.anchorY}
          accent={accentColor}
          panel={theme?.panel_fill ?? PANEL_FALLBACK}
        />
      ) : null}

      {usesSlot &&
      !isTreated &&
      template.imageBox !== null &&
      backgroundUrl !== null &&
      !isCommunity ? (
        <div
          className="slide-canvas__slot"
          style={{
            left: `${template.imageBox.left * 100}%`,
            top: `${template.imageBox.top * 100}%`,
            width: `${template.imageBox.width * 100}%`,
            height: `${template.imageBox.height * 100}%`,
            backgroundImage: `url("${backgroundUrl}")`,
          }}
          aria-hidden="true"
        />
      ) : null}

      {splitDecor()}
      {itemDecor()}
      {anniversaryDecor()}
      {communityDecor()}

      <div
        className="slide-canvas__scrim"
        style={{ ...overlay, ...scrimBoxStyle(template) }}
        aria-hidden="true"
      />

      {iconCodepoint !== null && !isCommunity ? (
        <IconGlyph codepoint={iconCodepoint} box={iconBox(layoutTemplate)} color={accentColor} />
      ) : null}

      {showVerticalGuide ? (
        <span className="slide-canvas__guide slide-canvas__guide--vertical" aria-hidden="true" />
      ) : null}
      {showHorizontalGuide ? (
        <span className="slide-canvas__guide slide-canvas__guide--horizontal" aria-hidden="true" />
      ) : null}

      {hasPhotoLayer && firstItem !== null ? (
        <div
          className={layerClassName('anniv-photo', photoDrag.isDragging, 'photo')}
          ref={photoRef}
          style={{
            left: `${effectivePhotoBox.x * 100}%`,
            top: `${effectivePhotoBox.y * 100}%`,
            width: `${effectivePhotoBox.w * 100}%`,
            height: `${effectivePhotoBox.h * 100}%`,
            borderRadius: `${(effectivePhotoBox.w / 2) * 100}cqw ${
              (effectivePhotoBox.w / 2) * 100
            }cqw 0 0`,
          }}
          role="button"
          aria-roledescription="Draggable layer"
          aria-label="Photo. Drag to move it, or move it with the arrow keys; the corner handle resizes it."
          tabIndex={disabled ? -1 : 0}
          onFocus={() => onSelectLayer?.('photo')}
          {...photoDrag.handlers}
        >
          <AnniversaryPhoto
            assetPath={firstItem.asset_path}
            title={firstItem.title}
            accent={accentColor}
          />
          <button
            className="slide-canvas__photo-handle"
            type="button"
            aria-label="Resize the photo"
            disabled={disabled}
            onPointerDown={startPhotoResize}
            onPointerMove={movePhotoResize}
            onPointerUp={endPhotoResize}
            onPointerCancel={endPhotoResize}
          />
        </div>
      ) : null}

      <div
        className={captionClassName()}
        ref={captionRef}
        style={captionStyle}
        role="button"
        aria-roledescription="Draggable layer"
        aria-label="Caption block. Drag it, or move it with the arrow keys."
        tabIndex={disabled ? -1 : 0}
        onFocus={() => onSelectLayer?.('caption')}
        {...caption.handlers}
      >
        {isSplit
          ? splitCaptionContent()
          : isAnniversary
            ? anniversaryCaptionContent()
            : stackCaptionContent()}
      </div>

      {hasLogoBox && effectiveLogoPoint !== null ? (
        <div
          className={layerClassName('logo', logo.isDragging, 'logo')}
          ref={logoRef}
          style={{
            left: `${effectiveLogoPoint.x * 100}%`,
            top: `${effectiveLogoPoint.y * 100}%`,
            width: `${(zoneLogoBox?.w ?? LOGO_SIZE) * 100}%`,
            height: `${(zoneLogoBox?.h ?? LOGO_SIZE) * 100}%`,
          }}
          role="button"
          aria-roledescription="Draggable layer"
          aria-label="Logo. Drag it, or move it with the arrow keys."
          tabIndex={disabled ? -1 : 0}
          onFocus={() => onSelectLayer?.('logo')}
          {...logo.handlers}
        >
          {logoUrl === null ? (
            <span className="slide-canvas__logo-chip">Logo</span>
          ) : (
            // draggable={false} is load-bearing: without it the browser starts
            // its own HTML5 image drag on the mark, the pointer gesture gets a
            // pointercancel, and the logo reads as immovable.
            <img className="slide-canvas__logo-image" src={logoUrl} alt="" draggable={false} />
          )}
        </div>
      ) : null}
    </div>
  );
}
